import { useState, useRef, useEffect } from 'react';
import { Label } from '../types/kanban';
import PlusIcon from './icons/PlusIcon';

interface LabelDropdownProps {
  labels: Label[];
  cardLabels: Label[];
  onAddLabel: (labelId: number) => Promise<boolean>;
  onRemoveLabel: (labelId: number) => Promise<boolean>;
  onCreateLabel: () => void;
}

export function LabelDropdown({
  labels,
  cardLabels,
  onAddLabel,
  onRemoveLabel,
  onCreateLabel,
}: LabelDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const cardLabelIds = new Set(cardLabels.map((l) => l.id));
  const availableLabels = labels.filter((l) => !cardLabelIds.has(l.id));

  const handleToggleLabel = async (labelId: number, isAdded: boolean) => {
    let success = false;
    if (isAdded) {
      success = await onRemoveLabel(labelId);
    } else {
      success = await onAddLabel(labelId);
    }
    if (success) {
      setIsOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white px-3 py-1.5 rounded-lg transition-colors text-sm"
      >
        <span className="w-4 h-4">
          <PlusIcon />
        </span>
        Add Label
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-2 w-72 bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50">
          <div className="p-4">
            <h4 className="text-white font-medium mb-3">Labels</h4>

            {/* Add new label button */}
            <button
              onClick={() => {
                onCreateLabel();
                setIsOpen(false);
              }}
              className="w-full mb-3 flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg transition-colors text-sm"
            >
              <span className="w-4 h-4">
                <PlusIcon />
              </span>
              Create new label
            </button>

            {/* Available labels */}
            {availableLabels.length > 0 ? (
              <div className="space-y-2">
                <p className="text-gray-400 text-xs uppercase tracking-wide">Available Labels</p>
                {availableLabels.map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handleToggleLabel(label.id, false)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-left"
                  >
                    <span
                      className="w-6 h-6 rounded-full"
                      style={{ backgroundColor: label.color }}
                    />
                    <span className="text-gray-200 text-sm">{label.name}</span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-sm">No labels available</p>
            )}

            {/* Current labels (can be removed) */}
            {cardLabels.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-700 space-y-2">
                <p className="text-gray-400 text-xs uppercase tracking-wide">Current Labels</p>
                {cardLabels.map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handleToggleLabel(label.id, true)}
                    className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-700 transition-colors text-left"
                  >
                    <span
                      className="w-6 h-6 rounded-full border-2 border-white"
                      style={{ backgroundColor: label.color }}
                    />
                    <span className="text-gray-200 text-sm">{label.name}</span>
                    <span className="ml-auto text-xs text-gray-400">Remove</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
